import {
    RawGetEmployeeGroupsRequest,
    RawGetEmployeeGroupsResponse,
} from './employee-groups.types';
import {spotheroGet} from 'api';
import {camelizeKeys, convertKeysToSnakeCase} from 'utils/format-strings';
import {sendSentryException} from 'utils/error-handling';
import {EntityId} from '@reduxjs/toolkit';
import {ApiResponse} from 'api/api.types';
import {AxiosRequestConfig} from 'axios';

const formatGetEmployeeGroupsRequest = ({
    facilityIds,
    offset,
    limit,
}: {
    facilityIds: EntityId[];
    offset: number;
    limit: number;
}): RawGetEmployeeGroupsRequest => {
    const formattedData = {
        facilityId: facilityIds,
        limit,
        offset,
        camelize: true,
    };

    return convertKeysToSnakeCase(formattedData);
};

const formatGetEmployeeGroupsResponse = (
    response: ApiResponse<RawGetEmployeeGroupsResponse>
) => {
    const camelizedReponse = camelizeKeys(response.data);
    const updatedResponse = camelizedReponse.data.results.map((group) => {
        const eventSlots = Array.isArray(group.eventSlots)
            ? group.eventSlots
            : [];

        return {
            ...group,
            eventSlots: eventSlots.map((slot) => ({
                ...slot,
                inventory: {
                    current: slot?.inventory?.current || 0,
                    max: slot?.inventory?.max || group?.defaultInventory,
                },
            })),
        };
    });

    const formattedResponse = {
        data: {results: updatedResponse},
        meta: camelizedReponse.meta,
    };

    return formattedResponse;
};

export interface GetEmployeeGroupsParams {
    facilityIds: EntityId[];
    limit: number;
    offset: number;
    handleAbortSignal?: (signal: AbortSignal) => void;
    abortSignal?: AbortSignal;
}

const getEmployeeGroups = async (
    facilityIds: EntityId[],
    limit: number = 25,
    offset: number,
    config: AxiosRequestConfig = {}
) => {
    const request = formatGetEmployeeGroupsRequest({
        facilityIds,
        offset,
        limit,
    });

    try {
        const response = await spotheroGet<RawGetEmployeeGroupsResponse>(
            'employee-groups/',
            request,
            {
                paramsSerializer: {
                    indexes: null,
                },
                ...config,
            }
        );

        const formattedResponse = formatGetEmployeeGroupsResponse(response);

        return formattedResponse;
    } catch (error) {
        sendSentryException(error);

        throw error;
    }
};

export default getEmployeeGroups;
